import { Author, Paper } from '../models/result-set';
import { ChannelState } from '../models/thread-updates';
import { CountedValue, Facet } from '../models/facets';
import {
  FacetName,
  facetNames,
  Order,
  PaperFilter,
  SyncStatus,
  WidgetClientState,
  WidgetState,
} from '../models/widget-state';
import { getAtOffset, SpeculatedArray } from '../utils/speculated-array';
import { Message, StateUpdateMessage } from '../models/threads';
import { PaperFinderUIState, UIStatePaper } from '../models/nora-ui-state';
import { relevanceJudgementsMapping } from '../models/relevance';

export function toWidgetClientState(
  papers: Array<Paper>,
  stateMessage: StateUpdateMessage,
  widgetState: WidgetState,
  allMessages: SpeculatedArray<Message>,
  query: string,
  channelState?: ChannelState,
): WidgetClientState {
  const prevStateMessage = getAtOffset(
    allMessages,
    stateMessage.prevStateOffsetInThread,
  ) as StateUpdateMessage | undefined;
  const nextStateMessage = getAtOffset(
    allMessages,
    stateMessage.nextStateOffsetInThread,
  ) as StateUpdateMessage | undefined;

  const filteredPapers = computeFilteredPapers(
    papers,
    widgetState.filters ?? [],
  );

  const orderedPapers = computeOrderedPaperList(
    filteredPapers,
    widgetState.ordering,
  );

  const numPages = Math.ceil(filteredPapers.length / widgetState.pageSize);
  const indexOfFirstPaperInPage =
    widgetState.currentPage * widgetState.pageSize;
  const indexOfLastPaperInPage =
    (widgetState.currentPage + 1) * widgetState.pageSize - 1;

  let syncStatus: SyncStatus = { type: 'reconnecting' };
  if (channelState?.type === 'error') {
    syncStatus = { type: 'error', errors: channelState.errors };
    // it's considered ok if it's either already connected or is connecting without any previous errors
  } else if (
    channelState?.type === 'ok' ||
    (channelState?.type === 'opening' &&
      channelState.previousErrors.length === 0)
  ) {
    syncStatus = { type: 'ok' };
  }

  return {
    ...widgetState,
    query,
    syncStatus,
    widgetId: stateMessage.widgetId,
    stateId: stateMessage.stateId,
    // for client state we want the papers to be already sorted and not let the
    // ui components compute this
    papers: orderedPapers,
    originalOrderedPapers: papers,
    numPages,
    indexOfFirstPaperInPage,
    indexOfLastPaperInPage,
    prevStateId: prevStateMessage?.stateId,
    nextStateId: nextStateMessage?.stateId,
  };
}

function norm(s: string): string {
  // normalize the string for sorting, we want to ignore white spaces and quotations
  return s.replace(/[\s"']/g, '');
}

export const computeOrderedPaperList = (
  papers: Array<Paper>,
  ordering: Order | null,
): Array<Paper> => {
  switch (ordering?.by) {
    case 'year': {
      return [...papers].sort((a, b) =>
        ordering?.isDescending ? b.year - a.year : a.year - b.year,
      );
    }
    case 'title': {
      return [...papers].sort((a, b) =>
        ordering?.isDescending
          ? norm(b.title).localeCompare(norm(a.title))
          : norm(a.title).localeCompare(norm(b.title)),
      );
    }
    case 'venue': {
      return [...papers].sort((a, b) =>
        ordering?.isDescending
          ? norm(b.venue).localeCompare(norm(a.venue))
          : norm(a.venue).localeCompare(norm(b.venue)),
      );
    }
    case 'citation': {
      return [...papers].sort((a, b) =>
        ordering?.isDescending
          ? (b.citationCount ?? 0) - (a.citationCount ?? 0)
          : (a.citationCount ?? 0) - (b.citationCount ?? 0),
      );
    }
    default: {
      return [...papers];
    }
  }
};

// filters the papers based on the filters
// if facetNameToNotFilter is set, it will not filter by that facet.
// because we dont want the count in this facet values to change while we select a value in it
export const computeFilteredPapers = (
  papers: Array<Paper>,
  filters: Array<PaperFilter>,
  facetNameToNotFilter?: FacetName,
): Array<Paper> => {
  const filterByYears = filters
    .filter((f) => f.categoryName === 'year')
    .map((f) => parseInt(f.value))
    .filter((year) => !isNaN(year))
    .filter((year) => papers.some((p) => p.year === year));
  const filterByVenues = filters
    .filter((f) => f.categoryName === 'venue')
    .map((f) => f.value)
    .filter((venue) => papers.some((p) => p.venue === venue));
  const filterByAuthors = filters
    .filter((f) => f.categoryName === 'author')
    .map((f) => f.value)
    .filter((authorName) =>
      papers.some((p) => p.authors.some((a) => a.name === authorName)),
    );
  const filterByRelevance = filters
    .filter((f) => f.categoryName === 'relevance')
    .map((f) => parseInt(f.value))
    .filter((relevance) =>
      papers.some((p) => p.relevanceJudgement?.relevance === relevance),
    );
  const filterByCriteria = filters
    .filter((f) => f.categoryName === 'criteria')
    .map((f) => f.value)
    .filter((criteria) =>
      papers.some((p) =>
        p.relevanceJudgement?.relevanceCriteriaJudgements?.some(
          (c) => c.name === criteria && c.relevance === 3,
        ),
      ),
    );
  return papers.filter(
    (paper) =>
      (facetNameToNotFilter === 'year' ||
        filterByYears.length === 0 ||
        filterByYears.includes(paper.year)) &&
      (facetNameToNotFilter === 'venue' ||
        filterByVenues.length === 0 ||
        filterByVenues.includes(paper.venue)) &&
      (facetNameToNotFilter === 'relevance' ||
        filterByRelevance.length === 0 ||
        filterByRelevance.includes(paper.relevanceJudgement?.relevance ?? 0)) &&
      (facetNameToNotFilter === 'criteria' ||
        filterByCriteria.length === 0 ||
        filterByCriteria.some((criteria) =>
          paper.relevanceJudgement?.relevanceCriteriaJudgements?.some(
            (c) => c.name === criteria && c.relevance === 3,
          ),
        )) &&
      (facetNameToNotFilter === 'author' ||
        filterByAuthors.length === 0 ||
        filterByAuthors.some((authorName) =>
          paper.authors.some((a) => a.name === authorName),
        )),
  );
};

const incrementCount = (map: Map<string, number>, key: string): void => {
  const currentCount = map.get(key) ?? 0;
  map.set(key, currentCount + 1);
};

const sortTerms = (
  countedTerms: Map<string, number>,
  facetName: string,
): CountedValue[] => {
  return Array.from(countedTerms.entries())
    .map(([term, count]) => ({ term, count }))
    .sort((a, b) => {
      // Primary sort if relevance: by descending numeric value
      if (facetName === 'relevance') {
        return parseInt(b.term) - parseInt(a.term);
      }
      // Primary sort: by count descending
      if (b.count !== a.count) {
        return b.count - a.count;
      }

      // Secondary sort
      if (facetName === 'year') {
        // For 'year', sort by descending numeric value
        return parseInt(b.term) - parseInt(a.term);
      } else {
        // For other facets, sort alphabetically
        return a.term.localeCompare(b.term);
      }
    });
};

const extractFacetTerms = (
  papers: Paper[],
  filters: Array<PaperFilter>,
  facetName: FacetName,
): CountedValue[] => {
  const filteredPapers = computeFilteredPapers(papers, filters, facetName);

  const countedTerms = new Map<string, number>();
  filteredPapers.forEach((paper: Paper) => {
    if (facetName === 'year') {
      incrementCount(countedTerms, paper.year.toString());
    } else if (facetName === 'venue') {
      incrementCount(countedTerms, paper.venue);
    } else if (facetName === 'author') {
      paper.authors.forEach((author: Author) =>
        incrementCount(countedTerms, author.name),
      );
    } else if (facetName === 'relevance') {
      incrementCount(
        countedTerms,
        paper.relevanceJudgement?.relevance.toString() ?? '0',
      );
    } else if (facetName === 'criteria') {
      paper.relevanceJudgement?.relevanceCriteriaJudgements?.forEach(
        (criterion) => {
          if (criterion.relevance === 3) {
            incrementCount(countedTerms, criterion.name);
          }
        },
      );
    }
  });

  // Collect all unique terms for the facetName
  const allTerms = new Set<string>();
  papers.forEach((paper: Paper) => {
    if (facetName === 'year') {
      allTerms.add(paper.year.toString());
    } else if (facetName === 'venue') {
      allTerms.add(paper.venue);
    } else if (facetName === 'author') {
      paper.authors.forEach((author: Author) => allTerms.add(author.name));
    } else if (facetName === 'relevance') {
      allTerms.add(paper.relevanceJudgement?.relevance.toString() ?? '0');
    } else if (facetName === 'criteria') {
      paper.relevanceJudgement?.relevanceCriteriaJudgements?.forEach(
        (criterion) => {
          if (criterion.relevance === 3) {
            allTerms.add(criterion.name);
          }
        },
      );
    }
  });

  // Add terms with count 0 to countedTerms
  allTerms.forEach((term) => {
    if (!countedTerms.has(term)) {
      countedTerms.set(term, 0);
    }
  });

  const sortedTerms = sortTerms(countedTerms, facetName);

  // Add labels to the relevance terms
  if (facetName === 'relevance') {
    const termsWithLabels = sortedTerms.map((term) => {
      const relevanceMappingItem = relevanceJudgementsMapping.find(
        (judgement) => judgement.relevance.toString() === term.term,
      );
      return {
        ...term,
        label: relevanceMappingItem?.description,
        colorKey: relevanceMappingItem?.colorKey,
      };
    });
    return termsWithLabels;
  } else if (facetName === 'criteria') {
    const termsWithColor: CountedValue[] = sortedTerms.map((term) => ({
      ...term,
      colorKey: 'perfectlyRelevant',
    }));
    return termsWithColor;
  }
  return sortedTerms;
};

const notAllowedFacetValues = ['0', 'unknown', ''];

export const extractFacetsWithCountedValues = (
  papers: Paper[],
  filters: Array<PaperFilter>,
): Facet[] => {
  // Helper function to create a facet object with filtered values
  const extractFacetWithCountedValues = (facetName: FacetName): Facet => ({
    facetName,
    values: extractFacetTerms(papers, filters, facetName).filter(
      (facet) => !notAllowedFacetValues.includes(facet.term),
    ),
  });

  return facetNames.map(extractFacetWithCountedValues);
};

export function toNoraUIState(
  clientState: WidgetClientState,
): PaperFinderUIState {
  const selectPapers = new Set(clientState.selectedPapers);
  const { indexOfFirstPaperInPage, indexOfLastPaperInPage, papers } =
    clientState;

  const interestingPapers: Array<UIStatePaper> = [];
  for (let i = 0; i < papers.length; i++) {
    const paper = papers[i];
    const isSelected = selectPapers.has(paper.corpusId);
    const isVisible =
      i >= indexOfFirstPaperInPage && i <= indexOfLastPaperInPage;

    // only consider what is in view or was selected previously as interesting
    // we don't save other papers to the nora ui state
    if (isVisible || isSelected) {
      const corpusId = paper.corpusId;
      const paperTitle = paper.title;
      const paperYear = paper.year;

      interestingPapers.push({
        corpusId,
        isSelected,
        isVisible,
        paperTitle,
        paperYear,
      });
    }
  }

  return {
    id: clientState.widgetId,
    stateId: clientState.stateId,
    papers: interestingPapers,
  };
}
